import { useCallback, useEffect, useState } from "react";
import { useGetCellKpiDetailsMutation } from "../../../store/api/devices/deviceApiSlice";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { ICellNameKpiCell, ICellTypeArgs, IFullDeviceDetails } from "../../../store/api/deviceConfig/types";
import { useSelector } from "react-redux";
import { cellKpisColumns, nrCellKpisColumns } from "../../../components/Shared/Table/Columns";
import {
  useGetKpisByCellNameMutation,
  useGetLteCellByIdMutation,
  useGetNrCellByIdMutation,
  useGetZoneCellsQuery,
} from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { useStoreDispatch } from "../../../store/hooks";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { ICellArg } from "../../../store/api/devices/types";
import { toTimespanRangeMs } from "../../../store/helpers/helpers";
import { errorMessage } from "../../../utils/helpers";
import { searchKeywordSelector } from "../../../store/search/selector";
import { API } from "../../../globals/globals";
import { useLocation } from "react-router-dom";
import { getZoneIdCellList } from "../../Configuration/helpers";

export const useEmbeddedCellDetails = () => {

  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const search = useSelector(searchKeywordSelector);



  const dispatch = useStoreDispatch();
  const [healthFilter, setHealthFilter] = useState("all");
  const [startDate, setStartDate] = useState(7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [initialData, setInitialData] = useState([]);

  useEffect(() => {
    if(search?.length > 0 && initialData?.length > 0) {
      const newArr = initialData?.filter((item : any) => item?.cell_name?.toLowerCase()?.includes(search?.toLowerCase()))
      setData([ ...newArr])
    }
    else setData([ ...initialData])

  }, [initialData, search])


  const [ getCellKpiDetails ] = useGetCellKpiDetailsMutation();

  const fetchCellKpiDetails = useCallback( async()  => {
    const timeSpan = toTimespanRangeMs(startDate);
    const args = {
      filters: `to=${timeSpan?.to}&from=${timeSpan?.from}&health=${healthFilter?.toUpperCase()}`,
      siteName: selectedDevice?.objectKey || "unknown",
      nodeType: selectedDevice?.nodeType === "SN" ? "lte" : "nr",
    };
    setIsLoading(true);
    try{
      let response: any
      response  = await getCellKpiDetails(args);
      if (response.error || !response.data) {
        throw response.error.data || "Unknown error";
      }
      if(Array.isArray(response?.data)) {
          setData(response?.data || []);
          setInitialData(response?.data || []);
          setIsLoading(false);
          // dispatch(
          //   setOpenToast({
          //     openToast: true,
          //     toastType: "success",
          //     message: "Cell Kpi Data has been fetched successfully",
          //   })
          // );
      }
    } catch(error: any) {
      setIsLoading(false);
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }

  }, [dispatch, getCellKpiDetails, healthFilter, selectedDevice?.nodeType, selectedDevice?.objectKey, startDate])

  useEffect(() => {
    fetchCellKpiDetails()
  }, [fetchCellKpiDetails, healthFilter, startDate])

  const [selectedRow, setSelectedRow] = useState(null);
  const [rowClicked, setRowClicked] = useState(false);

  return {
    selectedRow,
    setSelectedRow,
    rowClicked,
    setRowClicked,
    data,
    isLoading,
    cellKpisColumns,
    nrCellKpisColumns,
    healthFilter,
    setHealthFilter,
    selectedDevice,
    startDate, 
    setStartDate,
    setIsLoading
  };
};

export const useEmbeddedDrawer = (name: string) => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const locationPath = location?.pathname.split("/");
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const [isLoading, setIsLoading] = useState(true);
  const [cellData, setCellData] = useState<ICellNameKpiCell | null>(null);
  const [getKpisCellDetails] = useGetKpisByCellNameMutation();
  const [getLteCellById] = useGetLteCellByIdMutation();
  const [getNrCellById] = useGetNrCellByIdMutation();
  const [zones, setZones] = useState("");

  const zoneArgs = {
    filters: `page=${"0"}&size=${"100"}`,
    deviceKey: locationPath[2],
    deviceNode: selectedDevice?.nodeType,
  };
  const { data: zoneList, isLoading: isLoadingZones } = useGetZoneCellsQuery(zoneArgs, {
    pollingInterval: API.ENDPOINTS.LTEZONECELLS.pollingInterval || API.POLLING_INTERVAL,
  });

  const fetchCellData = useCallback(async (cellVal: string) => {
    try {
      if(cellVal.split("-")[0]?.toLowerCase() === "cell") {
        const cellArgs: ICellArg = {
          deviceKey: selectedDevice?.objectKey || "unknown",
          radioNodeId: Number(parseInt(cellVal?.split("-")[1])) || 0,
        };
        let response: any;
        if (selectedDevice?.nodeType === "SN") {
          response = await getLteCellById(cellArgs);
        } else {
          response = await getNrCellById(cellArgs);
        }
        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          setZones(getZoneIdCellList(response?.data?.index, zoneList?.data || []) || "");
          setCellData(response?.data || null);
          setIsLoading(false);
          // dispatch(
          //   setOpenToast({
          //     openToast: true,
          //     toastType: "success",
          //     message: "Successfully Fetched!!",
          //   })
          // );
        }
      }
      else  {
        const args: ICellTypeArgs = {
          type: selectedDevice?.nodeType === "SN" ? "LTE" : "NR",
          deviceKey: selectedDevice?.objectKey || "unknown",
          cellName: cellVal || "unknown",
        };
        let response: any;
        response = await getKpisCellDetails(args);
        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          setZones(getZoneIdCellList(response?.data?.index, zoneList?.data || []) || "");
          setCellData(response?.data || null);
          setIsLoading(false);
          // dispatch(
          //   setOpenToast({
          //     openToast: true,
          //     toastType: "success",
          //     message: "Successfully Fetched!!",
          //   })
          // );
        }
      }
      
    } catch (err: any) {
      setIsLoading(false);
      // dispatch(
      //     setOpenToast({
      //         openToast: true,
      //         toastType: "error",
      //         message: "Something went wrong!!",
      //     })
      // );
    }
  }, [dispatch, getKpisCellDetails, getLteCellById, getNrCellById, selectedDevice?.nodeType, selectedDevice?.objectKey, zoneList]);

  useEffect(() => {
    if (name && !isLoadingZones) {
      fetchCellData(name);
    }
  }, [fetchCellData, isLoadingZones, name]);

  return {
    cellData,
    isLoading,
    zones
  };
};
